import { ChannelType } from '@type/channelType';

export type LocalStorageItem = {
    /**
     * 포스트 새글 작성 중 임시 데이터
     */
    editorDraft: {
        keyParams?: {
            channelType: ChannelType;
        };
        value: {
            title: string;
            content: string;
        };
    };
    /**
     * 마지막 읽은 브리프의 날짜
     */
    lastReadBriefDate: {
        keyParams?: {};
        value: string;
    };
    /**
     * 문장 학습 컨트롤 옵션
     */
    sentenceControlOption: {
        keyParams?: {};
        value: {
            textAlwaysVisible: boolean;
            translationAlwaysVisible: boolean;
        };
    };
    /**
     * 사이드바 열림/닫힘 상태
     */
    sidebarState: {
        keyParams?: {};
        value: {
            isOpen: boolean;
        };
    };
    /**
     * 학습노트 플로팅 패널 마지막 위치 및 크기
     */
    learningNotebookPosition: {
        keyParams?: {};
        value: {
            left: number;
            top: number;
            width?: number;
            height?: number;
        };
    };
    /**
     * 학습노트 임시 저장
     */
    learningNotebookDraft: {
        keyParams?: {};
        value: {
            text: string;
        };
    };
    /**
     * 더리스닝 학습법 모달 확인 여부
     */
    learningGuideSeen: {
        keyParams?: {};
        value: {
            seen: boolean;
        };
    };
};

export type LocalStorageItemKey = keyof LocalStorageItem;

const getItem = <T extends LocalStorageItemKey>({
    key,
    keyParams,
}: {
    key: T;
    keyParams?: LocalStorageItem[T]['keyParams'];
}): LocalStorageItem[T]['value'] | null => {
    const itemKey = createItemKey(key, keyParams);
    const valueStr = localStorage.getItem(itemKey);

    if (!valueStr) {
        return null;
    }

    try {
        const value = JSON.parse(valueStr) as LocalStorageItem[T]['value'];
        return value;
    } catch (error) {
        console.error(error);
    }

    return null;
};

const createItemKey = <T extends LocalStorageItemKey>(
    key: T,
    keyParams?: LocalStorageItem[T]['keyParams'],
) => {
    const paramStr = keyParams ? `:${JSON.stringify(keyParams)}` : '';
    return `${key}${paramStr}`;
};

const setItem = <T extends LocalStorageItemKey>({
    key,
    keyParams,
    value,
}: {
    key: T;
    keyParams?: LocalStorageItem[T]['keyParams'];
    value: LocalStorageItem[T]['value'];
}) => {
    try {
        const itemKey = createItemKey(key, keyParams);
        localStorage.setItem(itemKey, JSON.stringify(value));
    } catch (error) {
        console.error(error);
    }
};

const deleteItem = <T extends LocalStorageItemKey>({
    key,
    keyParams,
}: {
    key: T;
    keyParams?: LocalStorageItem[T]['keyParams'];
}) => {
    const itemKey = createItemKey(key, keyParams);
    localStorage.removeItem(itemKey);
};

export default {
    getItem,
    setItem,
    deleteItem,
};
